import { useCallback, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { IssuerAddBuyBackModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddBuyBackModal.tsx';
import { IssuerAddDistributionModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddDistributionModal.tsx';
import {
  IssuerNavInfoModal,
  IssuerNavInfoModalProps,
} from '@/modules/issuer-dashboard/components/modals/IssuerNavInfoModal.tsx';
import IssuerEditClassModal, {
  IssuerUpdateClassModalProps,
} from '@/modules/issuer-dashboard/components/modals/IssuerUpdateClassModal.tsx';
import { useIssuerDeleteRegisterClassMutation } from '@/modules/issuer-dashboard/queries/registers.tsx';
import { ConfirmModal, DropdownMenuItem, ModalProviderContext } from 'ui';
import { IssuerRegisterClassResponse, RegisterType } from 'api';

export const useIssuerRegisterClassMenuOptions = (
  registerClass: IssuerRegisterClassResponse,
): DropdownMenuItem[] => {
  const { openModal } = useContext(ModalProviderContext);
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { registerId, registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const { mutateAsync: deleteClass } = useIssuerDeleteRegisterClassMutation();

  const {
    id,
    current_price_per_share,
    class_name,
    min_investment,
    transfer_lock_period,
    register_obj,
  } = registerClass;

  const handleOpenDistributionModal = useCallback(() => {
    openModal(IssuerAddDistributionModal, {
      registerCountry,
      register_id: +registerId,
      selectedClass: {
        label: class_name,
        value: {
          id: id,
          price_per_share: current_price_per_share,
          toString: () => class_name,
        },
      },
    });
  }, [openModal, registerCountry, registerId, class_name, id, current_price_per_share]);

  const items: DropdownMenuItem[] = [
    // {
    //   label: 'Invite investor',
    //   onClick: handleOpenInviteShareholderModal(registerClass),
    // },
    {
      label: getRegisterTypeStrings('dividend'),
      onClick: () => {
        handleOpenDistributionModal();
      },
    },
    {
      label: 'Delete',
      onClick: () => {
        openModal(ConfirmModal, {
          title: (
            <span>
              <span>Delete class </span>{' '}
              <span className="text-blue-700">{registerClass.class_name}</span>
              <span>?</span>
            </span>
          ),
          onConfirm: async () => {
            deleteClass(registerClass.id);
          },
        });
      },
    },
    {
      label: 'Edit',
      onClick: () => {
        openModal<IssuerUpdateClassModalProps>(IssuerEditClassModal, {
          id,
          class_name,
          min_investment,
          transfer_lock_period,
          current_price_per_share,
        });
      },
    },
    {
      label: `${getRegisterTypeStrings('nav')} history`,
      onClick: () => {
        openModal<IssuerNavInfoModalProps>(IssuerNavInfoModal, {
          register_class_id: id,
          register_obj: register_obj,
          class_name: class_name,
        });
      },
    },
  ];

  if (register_obj?.type === RegisterType.Company) {
    items.splice(1, 0, {
      label: 'Buy back',
      onClick: () => {
        openModal(IssuerAddBuyBackModal, {
          predefinedClassId: id,
          defaultAllowsFractionalShares: register_obj?.allows_fractional_shares === true,
        });
      },
    });
  }

  return items;
};
